import React from 'react';
import css from './SimilarProvider.module.css';
import {
  H3,
  Image,
  ImageAlignment,
  ImageTextLink,
  Paragraph,
  ProviderImage,
  StackedColumn,
  StackedColumnSpacing,
  StackedRow,
  StackedRowSpacing,
} from '../../../../../../core';
import {
  ProviderDetails,
  ProviderSummary,
  SlotDateGroup,
} from '../../../../../../api/models';
import {
  ArrowRightImage,
  AvailableTimeframeImage,
} from '../../../../../../core/assets/images';

export interface SimilarProviderProps {
  providerSummary: ProviderSummary;
  onCareProviderSelected: (
    newProviderDetails: ProviderDetails,
    availableAppointmentSlots?: SlotDateGroup[]
  ) => void;
}

const getAvailabilityString = (
  daysUntilNextAppt: string | undefined
): string => {
  if (daysUntilNextAppt === undefined) {
    return '';
  }

  if (daysUntilNextAppt === '0') {
    return 'Available on selected date';
  } else {
    return `Available within ${daysUntilNextAppt} ${
      daysUntilNextAppt === '1' ? 'day' : 'days'
    } of selected date`;
  }
};

const SimilarProvider: React.FC<SimilarProviderProps> = (
  props: SimilarProviderProps
) => {
  const onCareProviderSelected = () => {
    props.onCareProviderSelected(
      {
        practitionerId: props.providerSummary.provider?.id ?? '',
        cattailsProviderId:
          props.providerSummary.provider?.providerNumber ?? '',
        name: props.providerSummary.provider?.name ?? '',
      },
      props.providerSummary.availableAppointmentSlots
    );
  };

  return (
    <div>
      <StackedRow>
        <div className={css.providerImageContainer}>
          <ProviderImage
            cattailsProviderNumber={
              props.providerSummary.provider?.providerNumber
            }
          />
        </div>
        <StackedColumn spacing={StackedColumnSpacing.NoGap}>
          <div className={css.providerNameContainer}>
            <H3 text={props.providerSummary.provider?.name ?? ''} />
          </div>
          <StackedRow spacing={StackedRowSpacing.Tight}>
            <Image
              source={AvailableTimeframeImage}
              alternateText="Available timeframe"
              width={20}
              height={20}
            />
            <StackedColumn spacing={StackedColumnSpacing.Normal}>
              <Paragraph
                text={getAvailabilityString(
                  props.providerSummary.daysUntilNextAppt
                )}
              />
              <>
                {props.providerSummary.providerLocations &&
                props.providerSummary.providerLocations.length > 0
                  ? props.providerSummary.providerLocations.map((location) => (
                      <StackedColumn
                        key={location.locationId}
                        spacing={StackedColumnSpacing.NoGap}
                      >
                        <Paragraph
                          text={location.locationName ?? ''}
                          bold={true}
                        />
                        <Paragraph
                          text={location.locationAddress?.street1 ?? ''}
                        />
                        <Paragraph
                          text={`${location.locationAddress?.city}, ${location.locationAddress?.state} ${location.locationAddress?.zip}`}
                        />
                      </StackedColumn>
                    ))
                  : null}
              </>
              <ImageTextLink
                caption="Select Care Provider"
                imageSource={ArrowRightImage}
                imageAlignment={ImageAlignment.Right}
                onClick={() => onCareProviderSelected()}
                allowWrap={false}
              />
            </StackedColumn>
          </StackedRow>
        </StackedColumn>
      </StackedRow>
    </div>
  );
};

export default SimilarProvider;
